import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { NoteTaker } from "@dreamhub/designsystem/icons";
import useAccount from "@/entities/accounts/hooks";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage
} from "@/components/ui/breadcrumb";
import LogoIconContainer from "@/components/ui/logo-icon-container";
import Input from "@/components/ui/input";
import Text from "@/components/ui/text";
import { Switch } from "@/components/ui/switch";
import MultiInput from "@/components/controls/MultiInput";
import SingleSelect from "@/components/controls/SingleSelect";
import PageLayout from "@/pages/settings/components/PageLayout";
import { extractKeyValuePairs } from "@/pages/settings/pages/GeneralSettings";
import {
  useAccountSettings,
  useUpdateAccountSettingByKey
} from "@/entities/settings/hooks";
import {
  AUTO_JOIN_MODES,
  AutoJoinMode,
  JOIN_RULES_MAX_DOMAINS,
  JOIN_RULES_MAX_KEYWORDS,
  JOIN_RULES_MAX_KEYWORD_LENGTH,
  JoinRules,
  MEETING_BOT_DISPLAY_NAME_KEY,
  MEETING_BOT_DISPLAY_NAME_MAX_LENGTH,
  MEETING_BOT_JOIN_RULES_KEY,
  MEETING_BOT_STRATEGY_KEY,
  NOTE_TAKER_TRANSLATION_PREFIX,
  getRejectedValueMessage,
  normalizeDomain,
  normalizeDomains,
  normalizeKeywords,
  parseJoinRules,
  serializeJoinRules,
  toAutoJoinMode,
  toStrategyValue
} from "@/pages/settings/components/noteTaker/noteTaker";

const JOIN_RULES_PREFIX = `${NOTE_TAKER_TRANSLATION_PREFIX}.joinRules`;

type JoinRulesList =
  | "alsoInternalDomains"
  | "ignoredDomains"
  | "blockedDomains"
  | "skipTitleKeywords";

export const NoteTakerSettings: React.FC = () => {
  const { t } = useTranslation();
  const accountSettingsQuery = useAccountSettings();
  const updateSettingByKey = useUpdateAccountSettingByKey();

  const settings = accountSettingsQuery.data
    ? extractKeyValuePairs(accountSettingsQuery.data)
    : {};
  const autoJoinMode = toAutoJoinMode(settings[MEETING_BOT_STRATEGY_KEY]);
  const storedDisplayName = String(
    settings[MEETING_BOT_DISPLAY_NAME_KEY] ?? ""
  );
  const [displayName, setDisplayName] = useState(storedDisplayName);
  const accountName = useAccount().data?.name;
  const storedJoinRules = settings[MEETING_BOT_JOIN_RULES_KEY];
  const joinRules = parseJoinRules(storedJoinRules);
  // The list inputs keep their own rows; bumping this remounts them from the
  // stored rules after a failed or no-op save.
  const [joinRulesRevision, setJoinRulesRevision] = useState(0);
  // The rules every save builds on. Saves made before the stored rules reload
  // (clicking the toggle blurs a list) would otherwise drop each other's edits.
  const latestJoinRules = useRef(joinRules);

  useEffect(() => {
    setDisplayName(storedDisplayName);
  }, [storedDisplayName]);

  useEffect(() => {
    latestJoinRules.current = parseJoinRules(storedJoinRules);
  }, [storedJoinRules]);

  const autoJoinLabel = (mode: AutoJoinMode) =>
    t(`${NOTE_TAKER_TRANSLATION_PREFIX}.autoJoin.options.${mode}`);

  const saveSetting = (
    settingKey: string,
    value: string,
    onError?: () => void
  ) =>
    updateSettingByKey.mutate(
      { pathParams: { settingKey }, data: { value } },
      {
        onError: (error) => {
          onError?.();
          toast.error(
            getRejectedValueMessage(error) ??
              t(`${NOTE_TAKER_TRANSLATION_PREFIX}.updateError`)
          );
        }
      }
    );

  const resetJoinRulesInputs = () =>
    setJoinRulesRevision((revision) => revision + 1);

  const saveJoinRules = (changes: Partial<JoinRules>) => {
    const current = latestJoinRules.current;
    const next = { ...current, ...changes };
    const value = serializeJoinRules(next);
    if (value === serializeJoinRules(current)) {
      // Normalizing can make an edit a no-op (a duplicate, a different case);
      // the rows still show what was typed until they are rebuilt.
      resetJoinRulesInputs();
      return;
    }
    latestJoinRules.current = next;
    saveSetting(MEETING_BOT_JOIN_RULES_KEY, value, () => {
      latestJoinRules.current = parseJoinRules(storedJoinRules);
      resetJoinRulesInputs();
    });
  };

  const handleJoinRulesListSave = (list: JoinRulesList) => (values: string[]) =>
    saveJoinRules({
      [list]:
        list === "skipTitleKeywords"
          ? normalizeKeywords(values)
          : normalizeDomains(values)
    });

  const handleAutoJoinChange = (value: number | string) => {
    const mode = toAutoJoinMode(value);
    if (mode === autoJoinMode) return;
    saveSetting(MEETING_BOT_STRATEGY_KEY, toStrategyValue(mode));
  };

  const handleDisplayNameBlur = () => {
    const trimmed = displayName.trim();
    setDisplayName(trimmed);
    if (trimmed === storedDisplayName) return;
    saveSetting(MEETING_BOT_DISPLAY_NAME_KEY, trimmed, () =>
      setDisplayName(storedDisplayName)
    );
  };

  const controlsDisabled =
    accountSettingsQuery.isError || updateSettingByKey.isPending;
  // The rules only decide which meetings an auto-joining bot skips.
  const joinRulesInactive = autoJoinMode === AutoJoinMode.NEVER;
  const joinRulesDisabled = controlsDisabled || joinRulesInactive;

  const domainValidation = {
    customRules: [
      {
        validate: (value: string) => !value || normalizeDomain(value) !== null,
        message: t(`${JOIN_RULES_PREFIX}.invalidDomain`)
      }
    ]
  };

  const renderJoinRulesList = (list: JoinRulesList) => {
    const isKeywords = list === "skipTitleKeywords";
    return (
      <div className="w-[240px] shrink-0" data-test={`note-taker-${list}`}>
        <MultiInput
          // Rebuilt whenever the stored list changes, so the rows show the
          // normalized values that were saved.
          key={`${joinRulesRevision}:${joinRules[list].join("\n")}`}
          fieldName={`noteTaker_${list}`}
          initialValue={joinRules[list]}
          onSave={handleJoinRulesListSave(list)}
          maxEntries={
            isKeywords ? JOIN_RULES_MAX_KEYWORDS : JOIN_RULES_MAX_DOMAINS
          }
          maxLength={isKeywords ? JOIN_RULES_MAX_KEYWORD_LENGTH : undefined}
          validation={isKeywords ? undefined : domainValidation}
          addNewText={t(
            `${JOIN_RULES_PREFIX}.${isKeywords ? "addKeyword" : "addDomain"}`
          )}
          placeholder={t(
            `${JOIN_RULES_PREFIX}.${
              isKeywords ? "keywordPlaceholder" : "domainPlaceholder"
            }`
          )}
          // Blocked domains stay editable so an admin can set them before
          // turning auto-join on, rather than racing the first scheduled bot.
          disabled={
            list === "blockedDomains" ? controlsDisabled : joinRulesDisabled
          }
        />
      </div>
    );
  };

  const title = t(`${NOTE_TAKER_TRANSLATION_PREFIX}.title`);

  return (
    <div className="flex w-full flex-col" data-test="settings-note-taker-page">
      <div className="flex h-11 w-full items-center gap-1 border-b border-border-badge-chip-default px-2 py-[6px]">
        <LogoIconContainer
          index={-1}
          icon={<NoteTaker />}
          iconSize="5"
          className="h-7 w-7 rounded-lg"
          innerClassName="rounded-lg"
        />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbPage className="cursor-default px-1 py-0.5">
                <Text as="span" variant="body">
                  {title}
                </Text>
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>
      <PageLayout
        title={title}
        sections={[
          {
            title: t(`${NOTE_TAKER_TRANSLATION_PREFIX}.control.title`),
            subTitle: t(`${NOTE_TAKER_TRANSLATION_PREFIX}.control.subtitle`),
            isLoading: accountSettingsQuery.isLoading,
            items: [
              {
                title: t(`${NOTE_TAKER_TRANSLATION_PREFIX}.autoJoin.title`),
                subTitle: t(
                  accountSettingsQuery.isError
                    ? `${NOTE_TAKER_TRANSLATION_PREFIX}.loadError`
                    : `${NOTE_TAKER_TRANSLATION_PREFIX}.autoJoin.subtitle`
                ),
                customAction: (
                  <SingleSelect
                    fieldName="autoJoinMeetings"
                    options={AUTO_JOIN_MODES.map((mode) => ({
                      id: mode,
                      displayName: autoJoinLabel(mode)
                    }))}
                    selectedValue={autoJoinMode}
                    onSelection={handleAutoJoinChange}
                    formatter={(value) => autoJoinLabel(toAutoJoinMode(value))}
                    disabled={controlsDisabled}
                    className="text-start border border-border-border2 pl-0"
                    popoverTriggerClassName="w-[240px] min-h-8 h-9"
                    variant="dropdown"
                  />
                )
              }
            ]
          },
          {
            title: t(`${JOIN_RULES_PREFIX}.title`),
            subTitle: t(
              joinRulesInactive
                ? `${JOIN_RULES_PREFIX}.inactive`
                : `${JOIN_RULES_PREFIX}.subtitle`
            ),
            isLoading: accountSettingsQuery.isLoading,
            items: [
              {
                title: t(`${JOIN_RULES_PREFIX}.alsoInternal.title`),
                subTitle: t(`${JOIN_RULES_PREFIX}.alsoInternal.subtitle`),
                customAction: renderJoinRulesList("alsoInternalDomains")
              },
              {
                title: t(`${JOIN_RULES_PREFIX}.ignoredDomains.title`),
                subTitle: t(`${JOIN_RULES_PREFIX}.ignoredDomains.subtitle`),
                customAction: renderJoinRulesList("ignoredDomains")
              },
              {
                title: t(`${JOIN_RULES_PREFIX}.blockedDomains.title`),
                subTitle: t(`${JOIN_RULES_PREFIX}.blockedDomains.subtitle`),
                customAction: renderJoinRulesList("blockedDomains")
              },
              {
                title: t(`${JOIN_RULES_PREFIX}.ignorePersonalEmail.title`),
                subTitle: t(
                  `${JOIN_RULES_PREFIX}.ignorePersonalEmail.subtitle`
                ),
                customAction: (
                  <Switch
                    data-test="note-taker-ignorePersonalEmail"
                    aria-label={t(
                      `${JOIN_RULES_PREFIX}.ignorePersonalEmail.title`
                    )}
                    checked={joinRules.ignorePersonalEmail}
                    onCheckedChange={(checked) =>
                      saveJoinRules({ ignorePersonalEmail: checked })
                    }
                    disabled={joinRulesDisabled}
                  />
                )
              },
              {
                title: t(`${JOIN_RULES_PREFIX}.skipTitleKeywords.title`),
                subTitle: t(`${JOIN_RULES_PREFIX}.skipTitleKeywords.subtitle`),
                customAction: renderJoinRulesList("skipTitleKeywords")
              }
            ]
          },
          {
            title: t(`${NOTE_TAKER_TRANSLATION_PREFIX}.personalization.title`),
            subTitle: t(
              `${NOTE_TAKER_TRANSLATION_PREFIX}.personalization.subtitle`
            ),
            isLoading: accountSettingsQuery.isLoading,
            items: [
              {
                title: t(`${NOTE_TAKER_TRANSLATION_PREFIX}.displayName.title`),
                subTitle: t(
                  `${NOTE_TAKER_TRANSLATION_PREFIX}.displayName.subtitle`
                ),
                customAction: (
                  <Input
                    data-test="note-taker-display-name"
                    aria-label={t(
                      `${NOTE_TAKER_TRANSLATION_PREFIX}.displayName.title`
                    )}
                    value={displayName}
                    maxLength={MEETING_BOT_DISPLAY_NAME_MAX_LENGTH}
                    placeholder={
                      accountName
                        ? t(
                            `${NOTE_TAKER_TRANSLATION_PREFIX}.displayName.defaultName`,
                            { accountName }
                          )
                        : undefined
                    }
                    disabled={controlsDisabled}
                    className="w-[240px] border-border-border2 h-9"
                    onChange={(e) => setDisplayName(e.target.value)}
                    onBlur={handleDisplayNameBlur}
                  />
                )
              }
            ]
          }
        ]}
      />
    </div>
  );
};
